import { Koi } from '@ksyos/koi';
import type { IServiceValidated } from './addServiceConsumerSettings';
import { ServiceCode } from '../../enums/serviceCode';
import { ServiceType } from '../../enums/serviceType';
import { SpirometerSoftwareNames } from '../../enums/spirometerSoftwareNames';
import { serviceSchema } from './addServiceConsumerSettings';

export interface IAddCarePathServiceProviderSettingsValidated extends IServiceProviderSettingsValidated {
    isDisabled: boolean;
    profileId?: string;
}
export interface IServiceProviderSettingsValidated {
    canSelectCareProviderAsServiceConsumer?: boolean;
    id: string;
    isAnalyst?: boolean;
    isBiometrician?: boolean;
    isGrader?: boolean;
    isGraderSelector?: boolean;
    isRequestCreator?: boolean;
    isRequestRecipient?: boolean;
    isServiceConsumerSelector?: boolean;
    sendNotificationEmployment?: boolean;
    sendNotificationPractitioner?: boolean;
    service: IServiceValidated;
    spirometerSoftwareName?: SpirometerSoftwareNames;
    usingRestEcgMeter?: boolean;
    usingSpirometer?: boolean;
}

export const serviceProviderSettingsAdd = Koi.object()
    .keys({
        canSelectCareProviderAsServiceConsumer: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('isServiceConsumerSelector', {
                is: true,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        id: Koi.string().uuid(),
        isAnalyst: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('service.code', {
                is: ServiceCode.TCOH,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        isBiometrician: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('isAnalyst', {
                is: true,
                otherwise: Koi.boolean().required(),
                then: Koi.valid(false),
            }),
        }),
        isGrader: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.boolean(),
        }),
        isGraderSelector: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('isBiometrician', {
                is: true,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        isRequestCreator: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('isBiometrician', {
                is: true,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        isRequestRecipient: Koi.when('service.serviceType', {
            is: Koi.allow(ServiceType.examination, ServiceType.consultation),
            otherwise: Koi.valid(false),
            then: Koi.boolean(),
        }),
        isServiceConsumerSelector: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('isRequestCreator', {
                is: true,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        sendNotificationEmployment: Koi.when('service.serviceType', {
            is: Koi.allow(
                ServiceType.examination,
                ServiceType.consultation,
                ServiceType.referral,
                ServiceType.referralSmall
            ),
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        sendNotificationPractitioner: Koi.when('service.serviceType', {
            is: Koi.allow(
                ServiceType.examination,
                ServiceType.consultation,
                ServiceType.referral,
                ServiceType.referralSmall
            ),
            otherwise: Koi.valid(false),
            then: Koi.boolean().required(),
        }),
        service: serviceSchema,
        spirometerSoftwareName: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(null),
            then: Koi.koi()
                .enum(SpirometerSoftwareNames)
                .when('usingSpirometer', { is: true, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
        }),
        usingRestEcgMeter: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            then: Koi.when('service.code', {
                is: ServiceCode.TCOR,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
        usingSpirometer: Koi.when('service.serviceType', {
            is: ServiceType.examination,
            otherwise: Koi.valid(false),
            then: Koi.when('service.code', {
                is: ServiceCode.TPO,
                otherwise: Koi.valid(false),
                then: Koi.boolean().required(),
            }),
        }),
    })
    .options({ convert: true });

export const addCarePathServiceProviderSettingsAdd = serviceProviderSettingsAdd.concat(
    Koi.object().keys({
        isDisabled: Koi.boolean(),
        profileId: Koi.string(),
    })
);
